import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Typography,
} from "@mui/material";
import useUpdate from "../../../api/useUpdate";
import { useTranslation } from "react-i18next";
import { TAB_KEY_TO_LABEL } from "../../constants/tabKeys";

const PERMISSION_GROUPS = [
  { title: "Main", tabs: ["dashboard", "users"] },
  { title: "Management", tabs: ["customers"] },
  { title: "Inventory", tabs: ["units"] },
  { title: "Accounts", tabs: ["accounts", "account_transactions", "account_transfers"] },
  { title: "Settings", tabs: ["currencies"] },
  { title: "Reports", tabs: ["reports"] },
];

const UserEdit = ({ open, onClose, editData, token, onSuccess }) => {
  const { t } = useTranslation();
  const { handleUpdate, loading, isSuccess, resetForm } = useUpdate("admin/users", token);

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isStaff, setIsStaff] = useState(false);
  const [allowedTabs, setAllowedTabs] = useState([]);

  const toggleTab = (tabKey) => {
    setAllowedTabs((prev) =>
      prev.includes(tabKey)
        ? prev.filter((k) => k !== tabKey)
        : [...prev, tabKey]
    );
  };

  useEffect(() => {
    if (editData) {
      setUsername(editData.username || "");
      setEmail(editData.email || "");
      setFirstName(editData.first_name || "");
      setLastName(editData.last_name || "");
      setPhoneNumber(editData.phone_number || "");
      setIsStaff(editData.is_staff || false);
      setAllowedTabs(
        Array.isArray(editData.allowed_tabs) ? [...editData.allowed_tabs] : []
      );
    }
  }, [editData]);

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("username", username);
    formData.append("email", email);
    formData.append("first_name", firstName);
    formData.append("last_name", lastName);
    formData.append("phone_number", phoneNumber);
    formData.append("is_staff", isStaff);
    formData.append("is_superuser", false);
    formData.append("allowed_tabs", JSON.stringify(allowedTabs));
    handleUpdate(editData.id, formData);
  };

  const canSubmit = username && allowedTabs.length > 0;

  useEffect(() => {
    if (resetForm) {
      onClose();
    }
  }, [resetForm, onClose]);

  useEffect(() => {
    if (isSuccess) {
      onSuccess?.();
      onClose();
    }
  }, [isSuccess, onClose, onSuccess]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("user.editUser")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            margin="dense"
            label={t("user.username")}
            fullWidth
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("user.email")}
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("user.firstName")}
            fullWidth
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("user.lastName")}
            fullWidth
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("user.phoneNumber")}
            fullWidth
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
          <TextField
            select
            margin="dense"
            label={t("user.accountType")}
            fullWidth
            value={isStaff}
            onChange={(e) => setIsStaff(e.target.value === "true")}
          >
            <MenuItem value={true}>{t("user.staff")}</MenuItem>
          </TextField>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              {t("user.tabPermissions", "Tab permissions (select tabs this user can access)")}
            </Typography>
            <FormGroup sx={{ pl: 0.5 }}>
              {PERMISSION_GROUPS.flatMap((group) => group.tabs).map((tabKey) => (
                <FormControlLabel
                  key={tabKey}
                  control={
                    <Checkbox
                      checked={allowedTabs.includes(tabKey)}
                      onChange={() => toggleTab(tabKey)}
                    />
                  }
                  label={
                    tabKey === "dashboard"
                      ? t("sidebar.dashboard", "Dashboard Overview")
                      : tabKey === "users"
                        ? t("sidebar.users", "System Users")
                        : tabKey === "customers"
                          ? t("sidebar.agents", "Agent")
                          : t(TAB_KEY_TO_LABEL[tabKey] || tabKey)
                  }
                  sx={{ width: "100%", ml: 0 }}
                />
              ))}
            </FormGroup>
            {allowedTabs.length === 0 && (
              <Typography variant="caption" color="error">
                {t("user.selectAtLeastOneTab", "Select at least one tab.")}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("user.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !canSubmit}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("user.save")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserEdit;
